import type { CSSProperties, FC } from 'react';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { Calendar } from 'lucide-react';

export type IpcDatePickerVariant = 'form' | 'inline' | 'cell';
export type IpcDateDisplayFormat = 'YYYY-MM-DD' | 'MM-DD';

export type IpcDatePickerProps = {
  value?: string | null;
  onChange?: (value: string | undefined) => void;
  variant?: IpcDatePickerVariant;
  /** 面板选中值始终为 YYYY-MM-DD；此为输入框展示格式 */
  format?: IpcDateDisplayFormat;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  status?: 'error';
  className?: string;
  autoFocus?: boolean;
  /** cell 场景：挂载后自动打开面板 */
  autoOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
  style?: CSSProperties;
};

function toDayjs(value?: string | null): Dayjs | null {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
}

function toIsoDate(d: Dayjs | null): string | undefined {
  if (!d || !d.isValid()) return undefined;
  return d.format('YYYY-MM-DD');
}

const DEFAULT_FORMAT: Record<IpcDatePickerVariant, IpcDateDisplayFormat> = {
  form: 'YYYY-MM-DD',
  inline: 'YYYY-MM-DD',
  cell: 'MM-DD',
};

/**
 * IPC 单日日期选择（原型 Showcase · Date）
 * - form: h-9 + 左侧 calendar 图标
 * - inline: 24px，用于页头 meta 等，不抬布局
 * - cell: 表格/里程碑高密度格，默认可 MM-DD
 */
export const IpcDatePicker: FC<IpcDatePickerProps> = ({
  value,
  onChange,
  variant = 'form',
  format,
  placeholder = '选择日期',
  allowClear = true,
  disabled = false,
  status,
  className,
  autoFocus = false,
  autoOpen = false,
  open,
  onOpenChange,
  placement,
  style,
}) => {
  const displayFormat = format ?? DEFAULT_FORMAT[variant];
  const size = variant === 'form' ? 'middle' : 'small';

  const picker = (
    <DatePicker
      value={toDayjs(value)}
      onChange={d => onChange?.(toIsoDate(d))}
      format={displayFormat}
      placeholder={placeholder}
      allowClear={allowClear}
      disabled={disabled}
      status={status}
      size={size}
      autoFocus={autoFocus}
      defaultOpen={autoOpen && open === undefined}
      open={open}
      onOpenChange={onOpenChange}
      placement={placement}
      style={style}
      getPopupContainer={() => document.body}
      className={[
        'ipc-date',
        `ipc-date--${variant}`,
        variant === 'form' ? 'ipc-date--with-icon' : '',
        className,
      ].filter(Boolean).join(' ')}
    />
  );

  if (variant !== 'form') return picker;

  return (
    <span className="ipc-date-shell ipc-date-shell--form">
      <Calendar size={14} className="ipc-date-shell__icon" aria-hidden />
      {picker}
    </span>
  );
};
